import React from 'react';

import { contentTypeAdminContent } from '@lumapps/content-types-admin-content/routes';
import type { ContentSave } from '@lumapps/designer-content-editor/types';
import type { ConfirmDialogProps } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { mdiArrowRight } from '@lumapps/lumx/icons';
import { FlexBox, Text, TextField, Typography } from '@lumapps/lumx/react';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { capitalizeFirstLetter } from '@lumapps/utils/string/capitalizeFirstLetter';

import { updateStatus } from '../api/contents';
import { CONTENTS } from '../keys';
import { Content, LegacyContentStatus } from '../types';

export const useValidationWorkflowActions = ({
    openConfirmDialog,
    contentId,
    saveContent,
    publishContent,
    canEdit,
    onSuccess,
}: {
    contentId?: string;
    saveContent: ContentSave;
    publishContent: () => Promise<Content>;
    canEdit: boolean;
    openConfirmDialog: (
        props: Pick<ConfirmDialogProps, 'title' | 'id' | 'size' | 'confirmMessage' | 'onConfirm' | 'confirmProps'>,
    ) => void;
    onSuccess: (content: Content) => void;
}) => {
    const { success, error } = useNotification();
    const { translateKey } = useTranslate();
    const comment = React.useRef('');
    const { redirect } = useRouter();

    const acceptPublication = React.useCallback(async () => {
        if (canEdit) {
            try {
                const updatedContent = await publishContent();

                onSuccess(updatedContent);
            } catch (err) {
                // Error notification is handled by the publish function.
            }
        } else {
            if (!contentId) {
                return;
            }
            try {
                const { data: updatedContent } = await updateStatus({ contentId, status: LegacyContentStatus.live });

                success({ content: translateKey(CONTENTS.CONTENT_PUBLISHED) });
                onSuccess(updatedContent);
            } catch (err) {
                error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
            }
        }
    }, [canEdit, contentId, error, onSuccess, publishContent, success, translateKey]);

    const requestRevision = React.useCallback(async () => {
        if (!contentId) {
            return;
        }
        try {
            // Send back to draft with a comment, the backend will apply the correct REFUSED status as a side effect
            const { data: updatedContent } = await updateStatus({
                contentId,
                comment: comment.current,
                status: LegacyContentStatus.draft,
            });

            success({ content: translateKey(CONTENTS.REVISION_REQUESTED) });
            onSuccess(updatedContent);
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    }, [comment, contentId, error, onSuccess, success, translateKey]);

    const onRequestRevision = React.useCallback(() => {
        openConfirmDialog({
            title: translateKey(CONTENTS.REQUEST_REVISION_LABEL),
            size: 'regular',
            confirmMessage: (
                <FlexBox>
                    <Text as="p" typography={Typography.body1}>
                        {translateKey(CONTENTS.REQUEST_REVISION_CONFIRM_DIALOG_CONTENT)}
                    </Text>
                    <TextField
                        multiline
                        label={capitalizeFirstLetter(translateKey(GLOBAL.COMMENT))}
                        onChange={(value) => {
                            comment.current = value;
                        }}
                    />
                </FlexBox>
            ),
            onConfirm: requestRevision,
            confirmProps: { label: translateKey(CONTENTS.REQUEST_REVISION_LABEL) },
            id: 'request-revision',
        });
    }, [openConfirmDialog, requestRevision, translateKey]);

    const submitContentForReview = React.useCallback(async () => {
        try {
            const savedContent = await saveContent({
                shouldNotTriggerLegacySuccess: true,
                shouldBlockLegacyRedirections: true,
            });

            await updateStatus({
                contentId: savedContent.id!,
                status: LegacyContentStatus.toValidate,
            });

            success({ content: translateKey(CONTENTS.SUBMITTED_FOR_REVIEW_MESSAGE) });

            if (savedContent.customContentType) {
                redirect(contentTypeAdminContent(savedContent.customContentType));
            }
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    }, [error, redirect, saveContent, success, translateKey]);

    const onSubmitForReview = React.useCallback(() => {
        openConfirmDialog({
            title: translateKey(CONTENTS.SUBMIT_FOR_REVIEW_LABEL),
            confirmMessage: (
                <Text as="p" typography={Typography.body1}>
                    {translateKey(CONTENTS.SUBMIT_FOR_REVIEW_CONFIRM_DIALOG_CONTENT)}
                </Text>
            ),
            onConfirm: submitContentForReview,
            confirmProps: { label: translateKey(GLOBAL.SUBMIT), leftIcon: mdiArrowRight },
            id: 'submit-for-review',
        });
    }, [openConfirmDialog, submitContentForReview, translateKey]);

    return {
        onAccept: acceptPublication,
        onRequestRevision,
        onSubmitForReview,
    };
};
